import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useContactChannel } from '@/hooks/useContactChannel';

/**
 * PrivacyConsentNotice
 * ────────────────────────────────────────────────────────────────────────
 * Short, GDPR/DPDP-style consent line for lead-generation forms.
 *
 * The Privacy Policy URL is resolved from the current vertical:
 *   • Study Abroad routes  → /study-abroad/privacy-policy/
 *   • Everything else      → /privacy-policy
 *
 * Two tones:
 *   tone="light" (default)  → for white/light form surfaces (gray text)
 *   tone="dark"             → for navy/dark form surfaces (slate-300 text)
 *
 * Place this directly BEFORE the form's submit button so the user reads it
 * the moment they're about to submit.
 */
const PrivacyConsentNotice = ({ tone = 'light', className = '', testid = 'privacy-consent-notice' }) => {
  const channel = useContactChannel();
  const isSA = channel?.vertical === 'study_abroad';
  const href = isSA ? '/study-abroad/privacy-policy/' : '/privacy-policy';
  const label = isSA ? 'Study Abroad Privacy Policy' : 'Privacy Policy';

  const isDark = tone === 'dark';
  const baseText = isDark ? 'text-slate-400' : 'text-gray-500';
  const linkText = isDark
    ? 'text-[#FACC15] hover:text-[#C5A028]'
    : 'text-[#003087] hover:text-[#C5A028]';
  const iconColour = isDark ? 'text-[#C5A028]' : 'text-[#003087]/70';

  return (
    <p
      className={`flex items-start gap-2 text-[11.5px] sm:text-xs leading-relaxed ${baseText} ${className}`}
      data-testid={testid}
    >
      <Lock className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${iconColour}`} aria-hidden="true" />
      <span>
        By submitting this form, you agree to be contacted by ZAFCO and acknowledge our{' '}
        <Link
          to={href}
          className={`font-semibold underline decoration-[#C5A028]/60 underline-offset-2 transition-colors ${linkText}`}
          data-testid={`${testid}-link`}
        >
          {label}
        </Link>
        . Your information is kept confidential and used only to respond to your enquiry.
      </span>
    </p>
  );
};

export default PrivacyConsentNotice;
